import { useEffect, useState, useRef } from 'react';
import { X, ExternalLink, Download, Loader2, Pin, PinOff, Mail } from 'lucide-react';
import { apiDownloadUrl } from '../lib/api';
import { renderAsync } from 'docx-preview';

interface AttachmentPreviewModalProps {
  attachment: {
    id: string;
    filename: string;
    mime_type: string;
    pinned_from_date?: string | null;
    meeting_instance_id?: string | null;
  };
  onClose: () => void;
  canPin?: boolean;
  canUnpin?: boolean;
  isPinned?: boolean;
  onPin?: () => void;
  onUnpin?: (mode: 'immediate' | 'future') => void;
}

interface ParsedEmail {
  from: string;
  to: string;
  cc: string;
  subject: string;
  date: string;
  bodyHtml: string | null;
  bodyText: string;
}

/** Parse an .eml (RFC 822) file into headers and body */
function parseEml(raw: string): ParsedEmail {
  // Split headers from body at first blank line
  const headerEnd = raw.indexOf('\r\n\r\n');
  const altHeaderEnd = raw.indexOf('\n\n');
  let splitIdx: number;
  let lineBreak: string;

  if (headerEnd >= 0 && (altHeaderEnd < 0 || headerEnd <= altHeaderEnd)) {
    splitIdx = headerEnd;
    lineBreak = '\r\n';
  } else if (altHeaderEnd >= 0) {
    splitIdx = altHeaderEnd;
    lineBreak = '\n';
  } else {
    // No body
    return { from: '', to: '', cc: '', subject: '', date: '', bodyHtml: null, bodyText: raw };
  }

  const headerBlock = raw.slice(0, splitIdx);
  const bodyBlock = raw.slice(splitIdx + (lineBreak === '\r\n' ? 4 : 2));

  // Unfold continuation lines (lines starting with whitespace are continuations)
  const unfoldedHeaders = headerBlock.replace(/\r?\n[ \t]+/g, ' ');

  function getHeader(name: string): string {
    const re = new RegExp(`^${name}:\\s*(.*)$`, 'im');
    const m = unfoldedHeaders.match(re);
    return m ? m[1].trim() : '';
  }

  const contentType = getHeader('Content-Type');
  const from = getHeader('From');
  const to = getHeader('To');
  const cc = getHeader('Cc');
  const subject = getHeader('Subject');
  const date = getHeader('Date');

  // Check for multipart
  const boundaryMatch = contentType.match(/boundary="?([^";\s]+)"?/i);

  if (boundaryMatch) {
    const boundary = boundaryMatch[1];
    const parts = bodyBlock.split(`--${boundary}`);
    let bodyText = '';
    let bodyHtml: string | null = null;

    for (const part of parts) {
      if (part.startsWith('--') || part.trim() === '') continue;

      const partHeaderEnd = part.indexOf('\r\n\r\n');
      const partAltEnd = part.indexOf('\n\n');
      let pSplit: number;
      if (partHeaderEnd >= 0 && (partAltEnd < 0 || partHeaderEnd <= partAltEnd)) {
        pSplit = partHeaderEnd + 4;
      } else if (partAltEnd >= 0) {
        pSplit = partAltEnd + 2;
      } else {
        continue;
      }

      const partHeaders = part.slice(0, pSplit).toLowerCase();
      const partBody = part.slice(pSplit).trim();

      if (partHeaders.includes('text/html')) {
        bodyHtml = partBody;
      } else if (partHeaders.includes('text/plain') && !bodyText) {
        bodyText = partBody;
      }
    }

    return { from, to, cc, subject, date, bodyHtml, bodyText: bodyText || bodyBlock };
  }

  // Check if single-part HTML
  if (contentType.toLowerCase().includes('text/html')) {
    return { from, to, cc, subject, date, bodyHtml: bodyBlock, bodyText: '' };
  }

  return { from, to, cc, subject, date, bodyHtml: null, bodyText: bodyBlock };
}

export function AttachmentPreviewModal({
  attachment,
  onClose,
  canPin,
  canUnpin,
  isPinned,
  onPin,
  onUnpin,
}: AttachmentPreviewModalProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUnpinMenu, setShowUnpinMenu] = useState(false);
  const [parsedEmail, setParsedEmail] = useState<ParsedEmail | null>(null);
  const [docxBlob, setDocxBlob] = useState<Blob | null>(null);
  const docxContainerRef = useRef<HTMLDivElement>(null);

  const isImage = attachment.mime_type.startsWith('image/');
  const isPdf = attachment.mime_type === 'application/pdf';
  const isEml = attachment.mime_type === 'message/rfc822';
  const isDocx = attachment.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  const isPreviewable = isImage || isPdf || isEml || isDocx;

  useEffect(() => {
    if (!isPreviewable) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    if (isEml) {
      // Fetch as text and parse
      apiDownloadUrl(`/attachments/${attachment.id}/download`)
        .then(async (url) => {
          const resp = await fetch(url);
          const text = await resp.text();
          if (!cancelled) {
            setParsedEmail(parseEml(text));
            setBlobUrl(url);
          }
        })
        .catch(() => {
          if (!cancelled) setError('Failed to load email preview');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else if (isDocx) {
      apiDownloadUrl(`/attachments/${attachment.id}/download`)
        .then(async (url) => {
          const resp = await fetch(url);
          const blob = await resp.blob();
          if (!cancelled) {
            setDocxBlob(blob);
            setBlobUrl(url);
          }
        })
        .catch(() => {
          if (!cancelled) setError('Failed to load document preview');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else {
      apiDownloadUrl(`/attachments/${attachment.id}/download`)
        .then((url) => {
          if (!cancelled) setBlobUrl(url);
        })
        .catch(() => {
          if (!cancelled) setError('Failed to load preview');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [attachment.id, isPreviewable, isEml, isDocx]);

  // Render docx when blob and container are ready
  useEffect(() => {
    if (!docxBlob || !docxContainerRef.current) return;
    const container = docxContainerRef.current;
    container.innerHTML = '';
    renderAsync(docxBlob, container, undefined, {
      className: 'docx-preview',
      inWrapper: false,
      renderEndnotes: false,
      renderFootnotes: false,
      renderFooters: false,
      renderHeaders: false,
    }).catch(() => {
      setError('Failed to render document');
    });
  }, [docxBlob]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleOpenInNewTab = () => {
    if (blobUrl) {
      window.open(blobUrl, '_blank');
      onClose();
    }
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = attachment.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`bg-bg-secondary rounded-lg shadow-xl flex flex-col max-h-[90vh] ${
        isDocx || isEml ? 'w-full max-w-[900px]' : 'max-w-[90vw] w-full sm:w-auto'
      } min-w-[320px]`}>
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <span className="text-sm font-medium text-text-primary truncate flex-1">
            {attachment.filename}
          </span>
          {/* Pin/Unpin */}
          {isPinned && canUnpin && onUnpin && (
            <div className="relative">
              <button
                onClick={() => setShowUnpinMenu(!showUnpinMenu)}
                className="p-1 rounded-md text-accent hover:text-accent-hover hover:bg-bg-hover transition-colors"
                title="Unpin from series"
              >
                <PinOff size={14} />
              </button>
              {showUnpinMenu && (
                <div className="absolute right-0 top-full mt-1 z-30 bg-bg-surface border border-border rounded-lg shadow-lg py-1 w-52">
                  <button
                    onClick={() => { onUnpin('immediate'); setShowUnpinMenu(false); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover transition-colors"
                  >
                    Remove immediately
                    <span className="block text-[10px] text-text-muted mt-0.5">Gone from current + future meetings</span>
                  </button>
                  <button
                    onClick={() => { onUnpin('future'); setShowUnpinMenu(false); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover transition-colors"
                  >
                    Remove from future only
                    <span className="block text-[10px] text-text-muted mt-0.5">Keeps through this meeting</span>
                  </button>
                </div>
              )}
            </div>
          )}
          {canPin && !isPinned && onPin && (
            <button
              onClick={onPin}
              className="p-1 rounded-md text-text-muted hover:text-accent hover:bg-bg-hover transition-colors"
              title="Pin to series"
            >
              <Pin size={14} />
            </button>
          )}
          {blobUrl && isImage && (
            <button
              onClick={handleOpenInNewTab}
              className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
              title="Open in new tab"
            >
              <ExternalLink size={14} />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-4 min-h-[200px]">
          {loading ? (
            <Loader2 size={24} className="text-text-muted animate-spin" />
          ) : error ? (
            <p className="text-sm text-danger">{error}</p>
          ) : isEml && parsedEmail ? (
            <EmailPreview email={parsedEmail} />
          ) : isDocx && docxBlob ? (
            <div
              ref={docxContainerRef}
              className="w-full overflow-auto max-h-[70vh] bg-white rounded-lg p-6 text-black [&_table]:border-collapse [&_td]:border [&_td]:border-gray-300 [&_td]:p-1 [&_th]:border [&_th]:border-gray-300 [&_th]:p-1 [&_section:empty]:hidden [&_.docx-wrapper-footer]:hidden [&_footer]:hidden"
            />
          ) : isImage && blobUrl ? (
            <img
              src={blobUrl}
              alt={attachment.filename}
              className="max-w-full max-h-[70vh] object-contain rounded"
            />
          ) : isPdf && blobUrl ? (
            <iframe
              src={blobUrl}
              title={attachment.filename}
              className="w-full h-[70vh] rounded border border-border"
            />
          ) : (
            <div className="text-center space-y-3">
              <p className="text-sm text-text-muted">Preview not available for this file type</p>
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-accent text-white rounded-md hover:bg-accent-hover transition-colors"
              >
                <Download size={14} />
                Download
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {isPreviewable && blobUrl && (
          <div className="flex items-center justify-end px-4 py-2.5 border-t border-border">
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-bg-hover text-text-primary rounded-md hover:bg-bg-input transition-colors"
            >
              <Download size={12} />
              Download
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function EmailPreview({ email }: { email: ParsedEmail }) {
  const formattedDate = email.date
    ? (() => { try { return new Date(email.date).toLocaleString(); } catch { return email.date; } })()
    : '';

  return (
    <div className="w-full max-w-2xl text-left self-start">
      {/* Email headers */}
      <div className="space-y-1.5 mb-4 pb-4 border-b border-border">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-accent/20 text-accent flex items-center justify-center shrink-0 mt-0.5">
            <Mail size={14} />
          </div>
          <div className="flex-1 min-w-0">
            {email.subject && (
              <p className="text-sm font-semibold text-text-primary">{email.subject}</p>
            )}
            {email.from && (
              <p className="text-xs text-text-secondary mt-0.5">
                <span className="text-text-muted">From:</span> {email.from}
              </p>
            )}
            {email.to && (
              <p className="text-xs text-text-secondary">
                <span className="text-text-muted">To:</span> {email.to}
              </p>
            )}
            {email.cc && (
              <p className="text-xs text-text-secondary">
                <span className="text-text-muted">Cc:</span> {email.cc}
              </p>
            )}
            {formattedDate && (
              <p className="text-xs text-text-muted mt-1">{formattedDate}</p>
            )}
          </div>
        </div>
      </div>

      {/* Email body */}
      {email.bodyHtml ? (
        <div
          className="prose prose-sm prose-invert max-w-none bg-bg-card rounded-lg p-4 overflow-auto max-h-[50vh] text-text-primary [&_a]:text-accent"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(email.bodyHtml) }}
        />
      ) : (
        <pre className="text-sm text-text-primary whitespace-pre-wrap break-words bg-bg-card rounded-lg p-4 overflow-auto max-h-[50vh] font-sans">
          {email.bodyText}
        </pre>
      )}
    </div>
  );
}

/** Basic HTML sanitizer — strips script tags, event handlers, and dangerous elements */
function sanitizeHtml(html: string): string {
  return html
    // Remove script tags and their content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove event handler attributes
    .replace(/\s+on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, '')
    // Remove javascript: URLs
    .replace(/href\s*=\s*"javascript:[^"]*"/gi, 'href="#"')
    .replace(/href\s*=\s*'javascript:[^']*'/gi, "href='#'")
    // Remove style tags (can contain expressions)
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    // Remove iframe, object, embed tags
    .replace(/<(iframe|object|embed|form|input|button)\b[^>]*\/?>/gi, '')
    .replace(/<\/(iframe|object|embed|form|input|button)>/gi, '');
}
